import type { PdfField as CfrPdfField } from "@/section/cfr/types";
import type { PdfFieldRect } from "./types";

export interface PdfFieldMetaShape extends Record<string, unknown> {
  label?: string;
  value?: string;
  fontSize?: number;
  cfr?: {
    fieldKey?: string;
    fieldType?: CfrPdfField["field_type"];
    systemFieldType?: CfrPdfField["system_field_type"];
    fieldDestType?: CfrPdfField["field_dest_type"];
    isRequired?: boolean;
    isReadonly?: boolean;
  };
}

type Mutable<T> = {
  -readonly [K in keyof T]: T[K];
};

function normalizePage(page?: number | null) {
  if (typeof page === "number" && Number.isFinite(page) && page > 0) {
    return Math.floor(page);
  }
  return 1;
}

function normalizeRectCoordinates(field: CfrPdfField): Mutable<PdfFieldRect["rect"]> {
  const { position_data: data } = field;
  return {
    x: Number(data?.x ?? 0),
    y: Number(data?.y ?? 0),
    width: Number(data?.width ?? 0),
    height: Number(data?.height ?? 0),
  };
}

function toMeta(field: CfrPdfField): PdfFieldMetaShape {
  return {
    label: field.field_key ?? field.field_id,
    value: field.value ?? field.freeform_value ?? "",
    fontSize: field.position_data?.text_size,
    cfr: {
      fieldKey: field.field_key,
      fieldType: field.field_type,
      systemFieldType: field.system_field_type,
      fieldDestType: field.field_dest_type,
      isRequired: field.is_required,
      isReadonly: field.is_readonly,
    },
  };
}

export function cfrFieldToPdfFieldRect(field: CfrPdfField): PdfFieldRect {
  return {
    id: field.field_id,
    page: normalizePage(field.position_data?.page),
    rect: normalizeRectCoordinates(field),
    meta: toMeta(field),
  };
}

export function cfrFieldsToPdfFieldRects(fields: CfrPdfField[]): PdfFieldRect[] {
  return fields.map(cfrFieldToPdfFieldRect);
}

function resolveCfrMeta(rect: PdfFieldRect): PdfFieldMetaShape["cfr"] {
  const meta = rect.meta as PdfFieldMetaShape | undefined;
  return meta?.cfr ?? {};
}

function resolveValue(rect: PdfFieldRect): string | undefined {
  const meta = rect.meta as PdfFieldMetaShape | undefined;
  return typeof meta?.value === "string" ? meta.value : undefined;
}

function resolveFontSize(rect: PdfFieldRect): number | undefined {
  const meta = rect.meta as PdfFieldMetaShape | undefined;
  return typeof meta?.fontSize === "number" ? meta.fontSize : undefined;
}

export function pdfFieldRectToCfrField(rect: PdfFieldRect, base?: Partial<CfrPdfField>): CfrPdfField {
  const cfrMeta = resolveCfrMeta(rect);
  const value = resolveValue(rect);
  const page = normalizePage(rect.page);

  return {
    field_id: base?.field_id ?? rect.id,
    field_key: base?.field_key ?? cfrMeta?.fieldKey ?? rect.id,
    field_type: base?.field_type ?? cfrMeta?.fieldType ?? "system",
    system_field_type: base?.system_field_type ?? cfrMeta?.systemFieldType,
    field_dest_type: base?.field_dest_type ?? cfrMeta?.fieldDestType,
    position_data: {
      x: rect.rect.x,
      y: rect.rect.y,
      width: rect.rect.width,
      height: rect.rect.height,
      text_size: resolveFontSize(rect) ?? base?.position_data?.text_size,
      page,
    },
    is_required: base?.is_required ?? cfrMeta?.isRequired,
    is_readonly: base?.is_readonly ?? cfrMeta?.isReadonly,
    freeform_value: base?.freeform_value ?? value,
    value: base?.value ?? value,
  };
}

export function pdfFieldRectsToCfrFields(rects: PdfFieldRect[], existing: Record<string, CfrPdfField> = {}): CfrPdfField[] {
  return rects.map((rect) => pdfFieldRectToCfrField(rect, existing[rect.id]));
}

